// 3rd party imports
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';

// bernie
import { ContextStore, RouteStore } from 'bernie-core';

// shared-ui
import { ILocalization, withLocalization, LocalizedText } from '@shared-ui/localization-context';

// mobx
import { PzCampaignStore, PzStore, UserAuthStore, UserStore } from 'stores';

// ui
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkLink } from 'uitk-react-link';
import { UitkRadio, UitkRadioGroup } from 'uitk-react-switch';
import { UitkCard } from 'uitk-react-cards';
import { UitkMoreInfoTrigger } from 'uitk-react-more-info-trigger';
import { UitkIcon } from 'uitk-react-icons';

// components
import { PzSignUpForm } from './pz-signup-form';
import { PzInfoDialog } from 'components/signup/pz-info-dialog';
import { Program } from 'models';

interface PzSignUpProps {
  context?: ContextStore;
  l10n?: ILocalization;
  routeStore?: RouteStore;
  user?: UserStore;
  pzStore?: PzStore;
  pzCampaignStore?: PzCampaignStore;
  userAuth?: UserAuthStore;
  program?: Program;
}

interface PzSignUpState {
  pzAccountPresent: boolean;
}

@withLocalization
@inject('user', 'routeStore', 'context', 'pzStore', 'pzCampaignStore', 'userAuth')
@observer
export class PzSignUp extends React.Component<PzSignUpProps, PzSignUpState> {
  public constructor(props) {
    super(props);

    this.state = {
      pzAccountPresent: false,
    };

    const pzLocale = this.props.program.pzLocale || this.props.program.locale.toLowerCase();
    this.props.pzCampaignStore.fetchCampaign(this.props.program.name, this.props.context.locale, pzLocale);
  }

  private handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (value === 'yes') {
      this.setState({ pzAccountPresent: true });
    } else {
      this.setState({ pzAccountPresent: false });
    }
  };

  public render() {
    const { context, l10n, routeStore, user, userAuth, pzStore, pzCampaignStore, program } = this.props;
    const expediaGroupEmail = 'mailto:expediagroupaffiliates@expedia.com';

    const pzAccountPresent = () => {
      return (
        <>
          <UitkText>
            This feature is coming soon, please{' '}
            <UitkLink inline={true}>
              <a href={expediaGroupEmail}>email us</a>
            </UitkLink>{' '}
            to express interest.
          </UitkText>
        </>
      );
    };

    const campaignTerms = {
      terms: pzCampaignStore.campaignTerms,
      terms_id: pzCampaignStore.campaignTermsId,
      state: pzCampaignStore.campaignState,
    };

    const pzSignupForm = () => {
      return (
        <PzSignUpForm
          context={context}
          l10n={l10n}
          routeStore={routeStore}
          user={user}
          pzStore={pzStore}
          terms={pzStore.terms}
          termsId={pzStore.termsId}
          termsState={pzStore.termsState}
          campaignTerms={campaignTerms}
          userAuth={userAuth}
          program={program}
        />
      );
    };

    const pageTitle = this.props.l10n.formatText('pz.signup.title.join_program') + ' - ' + program.title;

    return (
      <>
        <UitkSpacing className="container" padding={{ block: 'twelve' }}>
          <main>
            <UitkSpacing padding={{ blockend: 'six' }}>
              <UitkHeading tag="h1" size={1} className="PzSignup__title">
                {pageTitle}
              </UitkHeading>
            </UitkSpacing>
            <UitkSpacing margin={{ blockstart: 'two', blockend: 'six' }}>
              <UitkCard tag="article" className="PzSignup__hero">
                <UitkHeading tag="h1" size={5}>
                  Commercial details
                </UitkHeading>
                <UitkMoreInfoTrigger>
                  <Link
                    to={{
                      pathname: routeStore.pathname,
                      search: routeStore.mergeQueryString(routeStore.search, {
                        pwaDialog: 'pzInfo',
                      }),
                    }}
                    role="button"
                  >
                    <LocalizedText
                      message="pz.signup.info.commercial.terms.item1"
                      data={[program.commissionPercentage]}
                    />
                    <UitkIcon name="info_outline" />
                  </Link>
                </UitkMoreInfoTrigger>
                <UitkText size={300}>
                  <LocalizedText message="pz.signup.info.commercial.terms.item2" data={[program.cookieDurationDays]} />
                </UitkText>
              </UitkCard>
            </UitkSpacing>
            <UitkSpacing padding={{ blockend: 'three' }} className="PzSignup__sub-title">
              <div>
                <UitkSpacing margin={{ blockend: 'three' }}>
                  <UitkHeading tag="h3" size={4}>
                    <LocalizedText message="pz.signup.title.account_required" />
                  </UitkHeading>
                </UitkSpacing>
                <UitkText inline={true}>
                  <LocalizedText message="pz.signup.text.third_party_use" />{' '}
                  <UitkLink inline={true}>
                    <a
                      href="https://egaffiliates.zendesk.com/hc/en-us/articles/1500003709001-FAQ-Partnerize-and-the-Affiliate-Hub-"
                      target="_blank"
                    >
                      <LocalizedText message="learn.more" />
                    </a>
                  </UitkLink>
                </UitkText>
              </div>
            </UitkSpacing>
            <UitkSpacing margin={{ blockstart: 'three' }}>
              <UitkHeading tag="h4" size={5}>
                <LocalizedText message="pz.signup.title.do_you_have_account" />
              </UitkHeading>
            </UitkSpacing>
            <UitkSpacing margin={{ blockstart: 'three', blockend: 'six' }}>
              <div>
                <UitkRadioGroup group="radioGroup" selected="no" onChange={this.handleRadioChange}>
                  <UitkRadio id="RadioChild1" value="yes" label="Yes" />
                  <UitkRadio id="RadioChild2" value="no" label="No" />
                </UitkRadioGroup>
              </div>
            </UitkSpacing>
            {this.state.pzAccountPresent ? pzAccountPresent() : pzSignupForm()}
          </main>
        </UitkSpacing>
        <PzInfoDialog
          dialogId="pzInfo"
          programTitle={program.title}
          commissionPercentage={program.commissionPercentage}
          cookieDuration={program.cookieDurationDays}
          l10n={l10n}
          routeStore={routeStore}
        />
      </>
    );
  }
}
